(() => {
  const SALE_DATA_SELECTOR = '[data-sale-variant-data]';
  const BADGE_CLASS = 'variant-sale-highlight-badge';

  /**
   * Berechnet den Rabatt einer Variante in Prozent.
   */
  function getDiscountPercent(variant) {
    const price = Number(variant.price);
    const compareAtPrice = Number(variant.compare_at_price);

    if (!compareAtPrice || compareAtPrice <= price) {
      return 0;
    }

    return Math.round((1 - price / compareAtPrice) * 100);
  }

  /**
   * Bestimmt den normalen Rabatt des Produkts.
   *
   * Regeln:
   *
   * 10 / 10 / 10 / 10
   * => nichts markieren
   *
   * 0 / 3
   * => 3 markieren
   *
   * 10 / 15
   * => 15 markieren
   *
   * 10 / 10 / 10 / 15
   * => 15 markieren
   *
   * 10 / 10 / 15 / 15
   * => beide 15 markieren
   *
   * 10 / 15 / 20 / 25
   * => nichts markieren
   */
  function findNormalDiscount(variants) {
    const discounts = variants.map(getDiscountPercent);

    if (discounts.length < 2) {
      return null;
    }

    const counts = new Map();

    discounts.forEach((discount) => {
      counts.set(discount, (counts.get(discount) || 0) + 1);
    });

    const uniqueDiscounts = Array.from(counts.keys()).sort(
      (a, b) => a - b
    );

    /**
     * Alle Varianten haben denselben Rabatt.
     *
     * 10 / 10 / 10 / 10
     * 0 / 0
     *
     * => nichts markieren
     */
    if (uniqueDiscounts.length === 1) {
      return null;
    }

    /**
     * Genau zwei Rabattstufen.
     *
     * Der niedrigere Rabatt ist die Basis.
     * Alles darüber wird hervorgehoben.
     *
     * 0 / 3
     * => 3 markieren
     *
     * 10 / 15
     * => 15 markieren
     *
     * 10 / 10 / 10 / 15
     * => 15 markieren
     *
     * 10 / 10 / 15 / 15
     * => beide 15 markieren
     */
    if (uniqueDiscounts.length === 2) {
      return uniqueDiscounts[0];
    }

    /**
     * Bei drei oder mehr Rabattstufen suchen wir
     * nach einem eindeutigen Normalrabatt.
     */
    let normalDiscount = null;
    let highestCount = 0;
    let hasTie = false;

    counts.forEach((count, discount) => {
      if (count > highestCount) {
        normalDiscount = discount;
        highestCount = count;
        hasTie = false;
      } else if (count === highestCount) {
        hasTie = true;
      }
    });

    /**
     * Keine klare Mehrheit:
     *
     * 10 / 15 / 20 / 25
     *
     * => nichts markieren
     */
    if (hasTie || highestCount < 2) {
      return null;
    }

    return normalDiscount;
  }

  /**
   * Liest die aktuell ausgewählten Optionswerte
   * in ihrer Reihenfolge aus.
   *
   * Beispiel:
   * ["Blue Pool", "S"]
   */
  function getSelectedOptions(variantSelects) {
    return Array.from(
      variantSelects.querySelectorAll('.product-form__input')
    ).map((optionGroup) => {
      const checkedRadio = optionGroup.querySelector(
        'input[type="radio"]:checked'
      );

      if (checkedRadio) {
        return checkedRadio.value;
      }

      const select = optionGroup.querySelector('select');

      if (select) {
        return select.value;
      }

      return null;
    });
  }

  /**
   * Findet die konkrete Variante, die entstehen würde,
   * wenn innerhalb einer Option ein anderer Wert
   * ausgewählt wird.
   */
  function findVariantForOption(
    variants,
    selectedOptions,
    optionIndex,
    candidateValue
  ) {
    const targetOptions = [...selectedOptions];

    targetOptions[optionIndex] = candidateValue;

    return variants.find((variant) => {
      if (!Array.isArray(variant.options)) {
        return false;
      }

      return variant.options.every(
        (optionValue, index) =>
          String(optionValue) === String(targetOptions[index])
      );
    });
  }

  /**
   * Entfernt vorhandene Badges.
   */
  function removeBadges(variantSelects) {
    variantSelects
      .querySelectorAll(`.${BADGE_CLASS}`)
      .forEach((badge) => badge.remove());
  }

  /**
   * Setzt Dropdown-Texte zurück.
   */
  function resetDropdownLabels(variantSelects) {
    variantSelects.querySelectorAll('select option').forEach((option) => {
      if (option.dataset.saleOriginalText) {
        option.textContent = option.dataset.saleOriginalText;
      }
    });
  }

  /**
   * Fügt einem Swatch oder Variantenbutton
   * das Rabatt-Badge hinzu.
   */
  function addBadgeToRadio(
    variantSelects,
    input,
    discount
  ) {
    if (!input.id) {
      return;
    }

    const label = variantSelects.querySelector(
      `label[for="${CSS.escape(input.id)}"]`
    );

    if (!label) {
      return;
    }

    const existingBadge = label.querySelector(
      `.${BADGE_CLASS}`
    );

    if (existingBadge) {
      existingBadge.remove();
    }

    const badge = document.createElement('span');

    badge.className = BADGE_CLASS;
    badge.textContent = `-${discount}%`;
    badge.setAttribute('aria-hidden', 'true');

    label.appendChild(badge);
  }

  /**
   * Fügt einem Dropdown-Wert den Rabatt hinzu.
   */
  function addBadgeToDropdownOption(
    option,
    discount
  ) {
    if (!option.dataset.saleOriginalText) {
      option.dataset.saleOriginalText =
        option.textContent.trim();
    }

    option.textContent =
      `${option.dataset.saleOriginalText} -${discount}%`;
  }

  /**
   * Aktualisiert die Sale-Markierungen
   * entsprechend der aktuell ausgewählten
   * Variantenkombination.
   */
  function updateBadges(variantSelects) {
    const data = variantSelects.saleHighlightData;

    if (!data) {
      return;
    }

    removeBadges(variantSelects);
    resetDropdownLabels(variantSelects);

    /**
     * Kein eindeutiger Normalrabatt:
     * nichts markieren.
     */
    if (data.normalDiscount === null) {
      return;
    }

    const selectedOptions =
      getSelectedOptions(variantSelects);

    if (
      !selectedOptions.length ||
      selectedOptions.some((value) => value === null)
    ) {
      return;
    }

    const optionGroups = Array.from(
      variantSelects.querySelectorAll(
        '.product-form__input'
      )
    );

    optionGroups.forEach(
      (optionGroup, optionIndex) => {

        /**
         * Buttons und Swatches
         */
        const radioInputs = Array.from(
          optionGroup.querySelectorAll(
            'input[type="radio"]'
          )
        );

        if (radioInputs.length) {
          radioInputs.forEach((input) => {
            const variant = findVariantForOption(
              data.variants,
              selectedOptions,
              optionIndex,
              input.value
            );

            if (!variant) {
              return;
            }

            const discount =
              getDiscountPercent(variant);

            if (discount > data.normalDiscount) {
              addBadgeToRadio(
                variantSelects,
                input,
                discount
              );
            }
          });

          return;
        }

        /**
         * Dropdowns
         */
        const select =
          optionGroup.querySelector('select');

        if (select) {
          Array.from(select.options).forEach(
            (option) => {

              const variant =
                findVariantForOption(
                  data.variants,
                  selectedOptions,
                  optionIndex,
                  option.value
                );

              if (!variant) {
                return;
              }

              const discount =
                getDiscountPercent(variant);

              if (
                discount > data.normalDiscount
              ) {
                addBadgeToDropdownOption(
                  option,
                  discount
                );
              }
            }
          );
        }
      }
    );
  }

  /**
   * Initialisiert einen einzelnen
   * Roar Variant Picker.
   */
  function analyseProduct(variantSelects) {
    if (!variantSelects) {
      return;
    }

    const dataElement =
      variantSelects.querySelector(
        SALE_DATA_SELECTOR
      );

    if (!dataElement) {
      return;
    }

    let variants;

    try {
      variants = JSON.parse(
        dataElement.textContent
      );
    } catch (error) {
      console.error(
        'Variant Sale Highlight: Variantendaten konnten nicht gelesen werden.',
        error
      );

      return;
    }

    if (
      !Array.isArray(variants) ||
      !variants.length
    ) {
      return;
    }

    variantSelects.saleHighlightData = {
      variants,
      normalDiscount:
        findNormalDiscount(variants)
    };

    updateBadges(variantSelects);
  }

  /**
   * Initialisiert alle Variant Picker
   * auf der aktuellen Seite.
   */
  function initialiseAllVariantPickers() {
    document
      .querySelectorAll('variant-selects')
      .forEach((variantSelects) => {
        analyseProduct(variantSelects);
      });
  }

  /**
   * Roar rendert den Variant Picker beim
   * Variantenwechsel teilweise neu.
   *
   * Deshalb beobachten wir neu eingefügte
   * Variant Picker.
   */
  let observerScheduled = false;

  const observer = new MutationObserver(
    (mutations) => {

      const relevantChange = mutations.some(
        (mutation) => {
          return Array.from(
            mutation.addedNodes
          ).some((node) => {

            if (!(node instanceof Element)) {
              return false;
            }

            return (
              node.matches?.(
                'variant-selects'
              ) ||
              node.querySelector?.(
                'variant-selects'
              )
            );
          });
        }
      );

      if (
        !relevantChange ||
        observerScheduled
      ) {
        return;
      }

      observerScheduled = true;

      requestAnimationFrame(() => {
        observerScheduled = false;

        initialiseAllVariantPickers();
      });
    }
  );

  /**
   * Variantenänderungen abfangen.
   *
   * Der Listener sitzt absichtlich auf
   * document, damit er bestehen bleibt,
   * wenn Roar den Variant Picker ersetzt.
   */
  document.addEventListener(
    'change',
    (event) => {

      const variantSelects =
        event.target.closest?.(
          'variant-selects'
        );

      if (!variantSelects) {
        return;
      }

      /**
       * Erste Aktualisierung direkt
       * nach der Auswahl.
       */
      requestAnimationFrame(() => {

        const currentVariantSelects =
          document.getElementById(
            variantSelects.id
          );

        if (currentVariantSelects) {
          analyseProduct(
            currentVariantSelects
          );
        }
      });

      /**
       * Zweite Aktualisierung nachdem
       * Roar seinen AJAX-/HTML-Refresh
       * abgeschlossen hat.
       */
      setTimeout(() => {

        const currentVariantSelects =
          document.getElementById(
            variantSelects.id
          );

        if (currentVariantSelects) {
          analyseProduct(
            currentVariantSelects
          );
        }

      }, 500);
    }
  );

  /**
   * Start
   */
  function init() {
    initialiseAllVariantPickers();

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();